import { AppliedDataFilter } from '@/components/filters/FilterButton';
import {
  PayoutTransactionFilterName,
  PayoutTransactionsFilter,
  transactionSortOptions,
} from '@/components/Payouts/PayoutTransactionsFilter';
import {
  PayoutTransactionTable,
  PayoutTransactionTableColumnName,
} from '@/components/Payouts/PayoutTransactionTable';
import { ProjectTransactionsButtonBar } from '@/components/Payouts/ProjectTransactionsButtonBar';
import { ProjectTransactionsLoading } from '@/components/Payouts/ProjectTransactionsLoading';
import { useGetProjectTransactionsByExternalIdQuery } from '@/services/payouts';
import { Box } from '@palmetto/palmetto-components';
import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ProjectTransactionSort, UserPermissions } from 'types';

import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';

export const AccountProjectTransactionTable = () => {
  const { id } = useParams<{ id: string }>();
  const { claims } = useAuth();
  const [appliedFilters, setAppliedFilters] = useState<AppliedDataFilter[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const isPalmettoFinanceReaderOrAdmin = useMemo(() => {
    return (
      isPalmettoFinanceUser(claims) &&
      (claims?.permissions?.includes(UserPermissions.lightReachAccountingAdmin) ||
        claims?.permissions?.includes(UserPermissions.lightReachAccountingReader))
    );
  }, [claims]);
  const advancedFilters = searchParams.get('advancedFilters') ?? '';
  const searchTerm = searchParams.get('search') || undefined;
  const sort = (searchParams.get('sort') as any) || 'eventDateAsc';
  const appliedSort = useMemo(
    () => (sort ? transactionSortOptions.find((opt) => opt.id === sort) : transactionSortOptions[0]),
    [sort],
  );

  const {
    data,
    isLoading: isTransactionsLoading,
    isFetching: isTransactionsFetching,
  } = useGetProjectTransactionsByExternalIdQuery({
    projectExternalId: id,
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(advancedFilters
      ? {
          advancedFilters: encodeURIComponent(advancedFilters || JSON.stringify([])) as any,
        }
      : {}),
    ...(appliedSort ? { sort: appliedSort.id } : {}),
  });
  const { transactions } = data ? data : { transactions: [] };

  const hideColumns = useMemo(() => {
    const columns: PayoutTransactionTableColumnName[] = [
      'project.name',
      'projectExternalId',
      'pauseExpirationDate',
      'transactionLevel',
    ];
    if (!isPalmettoFinanceReaderOrAdmin) {
      columns.push('payee.name');
      columns.push('tags');
    }
    return columns;
  }, [isPalmettoFinanceReaderOrAdmin]);

  const hideFilters = useMemo(() => {
    const columns: PayoutTransactionFilterName[] = [
      'projectName',
      'eventDate',
      'clawbackRuleName',
      'projectExternalId',
      'payoutHold',
      'isManual',
      'programType',
      'pausedDueToPaymentPlan',
    ];
    if (!isPalmettoFinanceReaderOrAdmin) {
      columns.push('payeeName');
      columns.push('payoutHold');
    }
    return columns;
  }, [isPalmettoFinanceReaderOrAdmin]);

  const hideSort = useMemo(() => {
    const sorts: ProjectTransactionSort[] = ['pausedExpirationDateAsc', 'pausedExpirationDateDesc'];
    if (!isPalmettoFinanceReaderOrAdmin) {
      sorts.push('payeeNameAsc', 'payeeNameDesc');
    }
    return sorts;
  }, [isPalmettoFinanceReaderOrAdmin]);

  const handleSortSelection = useCallback(
    (sortSelection: { id: string; label: string }) => {
      searchParams.set('sort', sortSelection.id);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  return (
    <Box gap="md" background="primary" radius="md" padding="0">
      <ProjectTransactionsButtonBar buttons={['addTransaction']} />
      <PayoutTransactionsFilter
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        searchTerm={searchTerm}
        advancedFilters={advancedFilters}
        handleSortSelection={handleSortSelection}
        appliedSort={appliedSort}
        hideSortOptions={hideSort}
        hideFilterOptions={hideFilters ?? []}
      />
      {isTransactionsLoading || isTransactionsFetching ? (
        <ProjectTransactionsLoading rows={3} />
      ) : (
        <PayoutTransactionTable
          transactions={transactions}
          hideColumns={hideColumns ?? []}
          showExpandedRow
          isCompact={false}
          showContextMenu
          disableSelection
        />
      )}
    </Box>
  );
};
